//@ts-nocheck

import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  PaymentProcessor,
  PaymentProcessorRef,
} from 'core/payment/payment-processor-provider/components/payment-processor';
import {
  billingSelectors,
  ProductDetails,
  selectNoFundsOfferEndDate,
  selectProducts,
  selectSpecialOfferProducts,
} from 'core/store/billing/selectors';
import { useAppSelector } from 'core/store/hooks';
import {
  initNoFundsOfferEndDate,
  initOrders,
  initSpecialOfferEndDate,
  setActiveProduct,
  setCountPPClose,
  setShowDownSaleModalOnce,
} from 'core/store/billing/actions';

import { SOLIDGATE_ERROR_CODES } from 'core/constants/errors';

import { Subscription } from 'core/interfaces/billing';

import Analytics from '../../../../module-analytics';
import { CallBacksType } from '../paymentProcessorContext';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { DownSaleOfferModal } from 'core/summary/down-sale-modal-offer';
import { useStore } from '../../../../../apps/virtual-brainary/src/onboarding/store/store';

type PaymentSubscriptionProps = {
  localPpRef?: React.Ref<PaymentProcessorRef>;
  callbacksRef:  React.RefObject<CallBacksType | null>;
};

export const PaymentSubscription: FC<PaymentSubscriptionProps> = ({ localPpRef, callbacksRef }) => {
  const downSaleModalRef = useRef(null);

  const {
    quizPaymentModalFullMode,
    isPaymentModalShowAfterQuiz,
    noFundsOffer,
    subscriptionPageType,
    specialOffer,
    openPaymentModalOnProductClick,
    products
  } = useRemoteConfig();
  const dispatch = useDispatch();

  // const products = useSelector(selectProducts);
  const noFundsOfferEndDate = useSelector(selectNoFundsOfferEndDate);
  const specialOfferProducts = useSelector(selectSpecialOfferProducts);
  const orderDetails = useAppSelector(billingSelectors.orderDetails);
  const activeProduct =
    useAppSelector(billingSelectors.activeProduct) ||
    products.find((p) => p.default) ||
    products[0] ||
    {};
  const showDownSaleModalOnce = useAppSelector(billingSelectors.showDownSaleModalOnce);
  const selectedSpecialOfferProduct = useAppSelector(
    (store) => store.billing.selectedSpecialOfferProduct
  );
  const countPPClosed = useAppSelector((store) => store.billing.countPPClosed);
  const email = useStore((state) => state.email);
  const isLoaded = useAppSelector((state) => !state.billing.loading);

  const handleOfferTriggered = useCallback(
    (type: 'specialOffer' | 'noFundsOffer') => {
      switch (type) {
        case 'noFundsOffer': {
          dispatch(initNoFundsOfferEndDate());
          break;
        }
        case 'specialOffer': {
          dispatch(initSpecialOfferEndDate());
          break;
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!showDownSaleModalOnce) {
      dispatch(
        setActiveProduct(
          products.find((p) => Object.values(p).includes(activeProduct?.id)) ||
            products.find((p) => p.default) ||
            products[0] ||
            activeProduct
        )
      );
    } else {
      dispatch(
        setActiveProduct(
          specialOfferProducts.find((p) => Object.values(p).includes(activeProduct?.id)) ||
            specialOfferProducts.find((p) => p.default) ||
            specialOfferProducts[0] ||
            activeProduct
        )
      );
    }
  }, [activeProduct, products, dispatch, showDownSaleModalOnce, specialOfferProducts]);

  const handleShowDownSaleModal = useCallback(() => {
    if (specialOffer.enabled && !showDownSaleModalOnce) {
      setTimeout(() => {
        if (selectedSpecialOfferProduct) {
          dispatch(setActiveProduct(selectedSpecialOfferProduct));
        }
        Analytics.trackEvent('special_offer_modal', 'open');
        // @ts-ignore
        // downSaleModalRef.current.showPaymentPopup();
        dispatch(setShowDownSaleModalOnce(true));
      }, 500);
    }
  }, [showDownSaleModalOnce, selectedSpecialOfferProduct]);

  const handleProductClick = useCallback(
    (product: ProductDetails) => {
      if (showDownSaleModalOnce) {
        dispatch(setActiveProduct(product));
      } else {
        dispatch(setActiveProduct(product));
      }

      if (openPaymentModalOnProductClick) {
        Analytics.trackEvent('product', 'click', { selectedProductId: product.id });
      }
    },
    [activeProduct, openPaymentModalOnProductClick, setActiveProduct]
  );

  const handlePPClose = useCallback(() => {
    dispatch(setCountPPClose(countPPClosed + 1));
    if (!showDownSaleModalOnce) {
      handleShowDownSaleModal();
    }
  }, [specialOfferProducts, selectedSpecialOfferProduct, showDownSaleModalOnce]);

  const handlePaymentError = useCallback(
    (error: any) => {
      const isSONoFundsTriggered =
        error?.code === SOLIDGATE_ERROR_CODES.INSUFFICIENT_FUNDS &&
        noFundsOffer.enabled &&
        !noFundsOfferEndDate &&
        error?.entity === 'form';

      if (!activeProduct?.isOneTimePurchase && isSONoFundsTriggered) {
        handleOfferTriggered('noFundsOffer');
      }
    },
    [activeProduct?.isOneTimePurchase, noFundsOffer.enabled, handleOfferTriggered]
  );

  const handleSuccess = (purchase: Subscription) => {
    callbacksRef.current?.onSuccess?.(purchase);
  };

  const isShowPopUpComponent = useMemo(
    () => isLoaded && selectedSpecialOfferProduct,
    [isLoaded, selectedSpecialOfferProduct]
  );
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (email) {
      dispatch(initOrders());
    }
  }, [email]);

  return (
    <>
      <PaymentProcessor
        ref={localPpRef}
        activeProduct={activeProduct}
        orderDetails={orderDetails}
        fullMode={quizPaymentModalFullMode}
        shownByDefault={isPaymentModalShowAfterQuiz}
        onSuccess={handleSuccess}
        onError={handlePaymentError}
        onClose={handlePPClose}
        isPurchaseProgress={false}
        subscriptionPageType={subscriptionPageType}
      />

      {isShowPopUpComponent && (
        <DownSaleOfferModal
          ref={downSaleModalRef}
          //@ts-ignore
          activeProduct={activeProduct}
          products={specialOfferProducts}
          onProductClick={handleProductClick}
        />
      )}
    </>
  );
};
